import { Link as BrowserLink } from 'react-router-dom';
import { Box, IconButton, Stack, styled, Typography } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { commonBorderLight, commonBorderLightGreen, commonBorderLightOrange, commonBorderLightRed } from 'themes';
import { blue, bgWeak, newGreen, newRed, stateFadedLight, textSecondary, warning, warningDark } from 'themes/colors';

export const StyledBrowserLink = styled(BrowserLink)`
  color: #4f83c5;
  text-decoration: none;
  font-weight: 400;
  font-size: 16px;
  line-height: 20px;
  -webkit-text-fill-color: #4f83c5;

  &:hover {
    text-decoration: underline;
  }
`;

export const StyledTabsFormTitle = styled(Box)(() => ({
  width: '100%',
  height: '60px',
  background: '#F3F4F6',
  fontFamily: 'SF Pro Text Regular',
  fontStyle: 'normal',
  fontWeight: '600',
  fontSize: '16px',
  lineHeight: '250%',
  color: '#111827',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingLeft: '20px',
  borderBottom: '1px solid #C0C7CA',
}));

export const StyledBox = styled(Box)(({ removePadding }: { removePadding?: boolean }) => ({
  width: '100%',
  padding: !removePadding ? '16px' : undefined,
}));

export const StyledDataGridWithToolbar = styled(DataGrid)`
  & .MuiDataGrid-columnHeaders {
    border-top: 1px solid #e0e0e0;
    border-radius: 0;
  }
`;

export const RowStack = styled(Stack, {
  shouldForwardProp: (prop) => prop !== 'pointer', // prevent pointer from going to DOM
})(
  ({
    width,
    fullWidth,
    spaceBetween,
    alignItems,
    flexDirection,
    pointer,
    alignCenter,
    wrap,
    scroll,
    justifyEnd,
    wrapText,
  }: {
    width?: number | string;
    fullWidth?: boolean;
    spaceBetween?: boolean;
    alignItems?: string;
    flexDirection?: string;
    pointer?: boolean;
    alignCenter?: boolean;
    wrap?: boolean;
    scroll?: boolean;
    justifyEnd?: boolean;
    wrapText?: boolean;
  }) => ({
    flexDirection: 'row',
    width: fullWidth ? '100%' : width || 'max-content',
    justifyContent: spaceBetween ? 'space-between' : undefined,
    alignItems: alignItems || 'flex-start',
    flexWrap: wrap ? 'wrap' : undefined,
    overflow: scroll ? 'auto' : undefined,
    ...(alignCenter && {
      alignItems: 'center',
    }),
    ...(justifyEnd && {
      justifyContent: 'flex-end',
    }),
    ...(pointer && {
      cursor: 'pointer',
    }),
    ...(wrapText && {
      textWrapMode: 'wrap',
    }),
  }),
);
export const StyledMainIcon = styled(Box)(() => ({
  height: '48px',
  width: '48px',
  border: commonBorderLight,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

export const StyledTextButton = styled(Typography)<{ underlined?: boolean; color?: string }>(
  ({ underlined = true, color = blue[400] }) => ({
    overflow: 'hidden',
    color: color,
    fontFeatureSettings: "'ss11' on, 'cv09' on, 'liga' off, 'calt' off",
    textOverflow: 'ellipsis',
    fontFamily: 'Inter',
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '24px',
    textDecorationLine: underlined ? 'underline' : 'none',
    textDecorationStyle: 'solid',
    textDecorationSkipInk: 'none',
    textDecorationThickness: 'auto',
    textUnderlineOffset: 'auto',
    textUnderlinePosition: 'from-font',
    cursor: 'pointer',
  }),
);

export const IconButtonCommon = styled(IconButton)(({ border = false }: { border?: boolean }) => ({
  padding: '0px',
  borderRadius: '0px',
  ...(border && {
    border: commonBorderLight,
    borderRadius: '4px',
  }),
}));

export const styledNewChipStatus = {
  info: {
    color: blue[400],
    backgroundColor: '#E6EEFF',
    border: `1px solid ${blue[100]}`,
  },
  orangeLight: {
    color: '#682F12',
    backgroundColor: '#FFF1EB',
    border: '1px solid #E9D7CF',
  },
  pink: {
    color: '#68123D',
    backgroundColor: '#FFEBF4',
    border: '1px solid #F4D5E3',
  },
  error: {
    color: newRed.errorDark,
    backgroundColor: newRed.errorLight,
    border: commonBorderLightRed,
  },
  errorBgDarkTextWhite: {
    color: 'white',
    backgroundColor: newRed.errorDark,
  },
  successDark: {
    color: newGreen.successDark,
    backgroundColor: 'white',
  },
  successLight: {
    color: newGreen.successDark,
    backgroundColor: newGreen.successLight,
    border: commonBorderLightGreen,
  },
  successLightDarker: {
    color: newGreen.successDarkDarker,
    backgroundColor: newGreen.successLightDarker,
  },
  successBgDarkTextWhite: {
    color: 'white',
    backgroundColor: newGreen.successDark,
  },
  white: {
    color: '#525866',
    backgroundColor: 'white',
    border: '1px solid #E1E4EA',
    '&:hover': {
      border: '1px solid #F5F7FA',
      backgroundColor: '#F5F7FA',
    },
  },
  default: {
    color: textSecondary,
    backgroundColor: stateFadedLight,
    border: commonBorderLight,
  },
  warning: {
    color: warning.warningDark,
    backgroundColor: warning.warningLight,
    border: commonBorderLightOrange,
  },
  orange: {
    color: 'white',
    backgroundColor: warningDark,
  },
  grey: {
    color: '#313132',
    backgroundColor: bgWeak,
  },
  purple: {
    color: '#351A75',
    backgroundColor: '#CAC0FF',
  },
  purpleLight: {
    color: '#351A75',
    backgroundColor: '#EFEBFF',
  },
  blue: {
    color: '#124B68',
    backgroundColor: '#C0EAFF',
  },
  whiteWithBorder: {
    color: 'black',
    backgroundColor: 'white',
    border: commonBorderLight,
  },
  lightOrange: {
    color: '#682F12',
    backgroundColor: '#FFF1EB',
    border: '1px solid #FFF1EB',
    fontWeight: 600,
    fontSize: '12px',
  },
};
export const StyledChipNew = styled(RowStack)<{
  status: keyof typeof styledNewChipStatus;
  isLink?: boolean;
  borderRadius?: string;
  padding?: string;
  smallSize?: boolean;
  rounded?: boolean;
  noUnderline?: boolean;
  underline?: boolean;
  underlineColor?: string;
}>(
  ({
    status = 'default',
    isLink = false,
    borderRadius = '6px',
    padding = '6px 12px',
    smallSize = false,
    rounded = false,
    noUnderline = false,
    underline = false,
    underlineColor = blue[400],
  }) => ({
    padding: '4px 12px 4px 8px',
    borderRadius: rounded ? '50px' : borderRadius,
    gap: '4px',
    ...(styledNewChipStatus[status] || {}),
    ...(isLink && {
      cursor: 'pointer',
      textDecoration: 'underline',
      textDecorationColor: blue[400],
      color: blue[400],
    }),
    ...(underline && {
      textDecoration: 'underline',
    }),
    ...(underlineColor && {
      textDecorationColor: underlineColor,
      cursor: 'pointer',
    }),
    ...(noUnderline && {
      textDecoration: 'none !important',
    }),
    alignItems: 'center',
  }),
);

// v5 menu
export const StyledPaperComponentContainer = styled(Box)(() => ({
  marginTop: '8px',
  backgroundColor: 'white',
  border: commonBorderLight,
  borderRadius: '8px',
  boxShadow: '0px 16px 32px -12px rgba(14, 18, 27, 0.10)',
  gap: '8px',
}));